/**
 * useAllProgramStatuses Hook
 * Fetches all program statuses without pagination
 * Used for Program Type form dropdown
 */

import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';

export interface ProgramStatus {
  program_status_id: number;
  program_status: string;
}

export interface UseAllProgramStatusesResult {
  programStatuses: ProgramStatus[];
  loading: boolean;
  error: string | null;
}

const GET_ALL_PROGRAM_STATUSES_QUERY = `
  query GetAllProgramStatuses {
    pep_program_status(where: {is_deleted: {_eq: false}}, order_by: {program_status: asc}) {
      program_status_id
      program_status
    }
  }
`;

export const useAllProgramStatuses = (): UseAllProgramStatusesResult => {
  const { hasuraToken } = useAuth();
  const [statuses, setStatuses] = useState<ProgramStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllStatuses = async () => {
      if (!hasuraToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await graphqlClient.query(
          GET_ALL_PROGRAM_STATUSES_QUERY,
          {},
          hasuraToken
        );

        if (response.success && response.data?.pep_program_status) {
          setStatuses(response.data.pep_program_status);
        } else {
          const errorMessage = response.errors?.join(', ') || 'Failed to fetch program statuses';
          setError(errorMessage);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch program statuses');
        console.error('Error fetching all program statuses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllStatuses();
  }, [hasuraToken]);

  return {
    programStatuses: statuses,
    loading,
    error
  };
};
